import Heading from "../blocks/Heading";
import Input from "../blocks/Input";
import PhoneInput from "../blocks/PhoneInput"; // ✅ Import your phone input
import { useAuth } from "../../context/AuthContext";
import { useEffect, useRef, useState } from "react";
import AuthLink from "../blocks/AuthLink";
import { validatePasswordChangeForm } from "../../utils/validation";
import { validatePassword } from "../../utils/validation";
import NotFound from "./notFound";
import type { ParsedCountry } from "../../types/PhoneInput"; // ✅ Import type
import CloseIcon from "../svgs/CloseIcon";

const Profile = () => {
  const {
    userAttributes,
    changeUserPassword,
    isLoading,
    error: authError,
    isAuthenticated,
    clearError,
    deleteProfile,
    isRedirecting,
    setIsRedirecting,
    updateUserAttributes, // ✅ Add this if not already there
    sendAttributeVerificationCode, // ✅ Add this if not already there
    verifyUserAttribute, // ✅ Add this if not already there
  } = useAuth();

  const firstName = userAttributes?.given_name || "User";
  const lastName = userAttributes?.family_name || "";
  const isEmailVerified = userAttributes?.email_verified === "true";
  const email = userAttributes?.email || "";

  // ✅ Phone attributes from Cognito
  const cogntoPhoneRaw = userAttributes?.phone_number || "";
  const isPhoneVerified = userAttributes?.phone_number_verified === "true";

  const phoneCountryCode = cogntoPhoneRaw.startsWith("+")
    ? cogntoPhoneRaw.slice(1).match(/^\d+/)?.[0] || ""
    : "";
  const phoneDigitsOnly = cogntoPhoneRaw
    .replace(/\D/g, "")
    .slice(phoneCountryCode.length);

  // ✅ Phone editing state
  const [editPhone, setEditPhone] = useState(0);
  const [phone, setPhone] = useState(phoneDigitsOnly);
  const [selectedCountry, setSelectedCountry] = useState<ParsedCountry | null>(
    null
  );
  const [phoneError, setPhoneError] = useState<string | null>(null);

  // ✅ Verification state
  const [verificationStep, setVerificationStep] = useState<
    "input" | "verify" | "success"
  >("input"); // input → verify → success
  const [verificationCode, setVerificationCode] = useState("");
  const [verificationError, setVerificationError] = useState<string | null>(
    null
  );
  const [verificationLoading, setVerificationLoading] = useState(false);
  const hasCheckedAuth = useRef(false);

  // Password state (existing code)
  const [editPassword, setEditPassword] = useState(0);
  const [currentPassword, setCurrentPassword] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [errors, setErrors] = useState<Record<string, string | null>>({
    currentPassword: null,
    newPassword: null,
    confirmPassword: null,
  });

  const [openPopup, setOpenPopup] = useState(false);
  const [touched, setTouched] = useState<Record<string, boolean>>({
    currentPassword: false,
    newPassword: false,
    confirmPassword: false,
  });

  // ✅ Set initial country from phone number on mount
  useEffect(() => {
    if (cogntoPhoneRaw && phoneCountryCode && !selectedCountry) {
      import("../../types/PhoneInput").then(({ getCountryByDialCode }) => {
        const country = getCountryByDialCode(phoneCountryCode);
        if (country) {
          setSelectedCountry(country);
        }
      });
    }
  }, [cogntoPhoneRaw]);

  const isFormValid =
    Object.values(errors).every((error) => !error) &&
    currentPassword.length > 0 &&
    newPassword.length > 0 &&
    confirmPassword.length > 0;

  useEffect(() => {
    return () => {
      setCurrentPassword("");
      setNewPassword("");
      setConfirmPassword("");
      setPhone(phoneDigitsOnly);
      setSelectedCountry(null);
      setErrors({
        currentPassword: null,
        newPassword: null,
        confirmPassword: null,
      });
      setTouched({
        currentPassword: false,
        newPassword: false,
        confirmPassword: false,
      });
    };
  }, []);

  const getValidationErrors = (
    current: string,
    newPass: string,
    confirm: string
  ) => {
    return validatePasswordChangeForm({
      currentPassword: current,
      newPassword: newPass,
      confirmPassword: confirm,
    });
  };

  // ✅ Get flag emoji
  const getFlagEmoji = (iso2: string) => {
    const codePoints = iso2
      .toUpperCase()
      .split("")
      .map((char) => 127397 + char.charCodeAt(0));
    return String.fromCodePoint(...codePoints);
  };

  // ✅ Display phone with flag (when not editing)
  const displayPhoneWithFlag = (): string => {
    if (!cogntoPhoneRaw) return "Not set";

    if (selectedCountry?.iso2) {
      const flag = getFlagEmoji(selectedCountry.iso2);
      return `${flag} ${cogntoPhoneRaw}`;
    }

    return cogntoPhoneRaw;
  };

  // Password handlers (existing code)
  const handleCurrentPasswordChange = (
    e: React.ChangeEvent<HTMLInputElement>
  ) => {
    const value = e.target.value;
    setCurrentPassword(value);
    if (touched.currentPassword) {
      const newError = validatePassword(value);
      setErrors((prev) => ({ ...prev, currentPassword: newError }));
    }
  };

  const handleCurrentPasswordBlur = () => {
    setTouched((prev) => ({ ...prev, currentPassword: true }));
    const newError = validatePassword(currentPassword);
    setErrors((prev) => ({ ...prev, currentPassword: newError }));
  };

  const handleNewPasswordChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    setNewPassword(value);
    if (touched.newPassword) {
      const newErrors = getValidationErrors(
        currentPassword,
        value,
        confirmPassword
      );
      setErrors((prev) => ({
        ...prev,
        newPassword: newErrors.newPassword,
        confirmPassword: newErrors.confirmPassword,
      }));
    }
  };

  const handleNewPasswordBlur = () => {
    setTouched((prev) => ({ ...prev, newPassword: true }));
    const newErrors = getValidationErrors(
      currentPassword,
      newPassword,
      confirmPassword
    );
    setErrors((prev) => ({
      ...prev,
      newPassword: newErrors.newPassword,
    }));
  };

  const handleConfirmPasswordChange = (
    e: React.ChangeEvent<HTMLInputElement>
  ) => {
    const value = e.target.value;
    setConfirmPassword(value);
    if (touched.confirmPassword) {
      const newErrors = getValidationErrors(
        currentPassword,
        newPassword,
        value
      );
      setErrors((prev) => ({
        ...prev,
        confirmPassword: newErrors.confirmPassword,
      }));
    }
  };

  const handleConfirmPasswordBlur = () => {
    setTouched((prev) => ({ ...prev, confirmPassword: true }));
    const newErrors = getValidationErrors(
      currentPassword,
      newPassword,
      confirmPassword
    );
    setErrors((prev) => ({
      ...prev,
      confirmPassword: newErrors.confirmPassword,
    }));
  };

  const handlePasswordUpdate = async (e: React.FormEvent) => {
    e.preventDefault();

    const finalErrors = getValidationErrors(
      currentPassword,
      newPassword,
      confirmPassword
    );
    setErrors(finalErrors);

    if (Object.values(finalErrors).some((error) => error)) {
      return;
    }

    clearError();

    try {
      const updatePromise = changeUserPassword(currentPassword, newPassword);
      const timeoutPromise = new Promise((_, reject) =>
        setTimeout(() => reject(new Error("Password update timeout")), 10000)
      );

      await Promise.race([updatePromise, timeoutPromise]);

      setCurrentPassword("");
      setNewPassword("");
      setConfirmPassword("");
      setErrors({
        currentPassword: null,
        newPassword: null,
        confirmPassword: null,
      });
      setTouched({
        currentPassword: false,
        newPassword: false,
        confirmPassword: false,
      });
      setEditPassword(0);
    } catch (err) {
      console.error("Password update failed:", err);
    }
  };

  const handleCancel = () => {
    setCurrentPassword("");
    setNewPassword("");
    setConfirmPassword("");
    setErrors({
      currentPassword: null,
      newPassword: null,
      confirmPassword: null,
    });
    setTouched({
      currentPassword: false,
      newPassword: false,
      confirmPassword: false,
    });
    clearError();
    setEditPassword(0);
  };

  const handlePhoneChange = (
    phoneValue: string,
    _formatted: string | null,
    country: ParsedCountry | null
  ) => {
    setPhone(phoneValue);
    setSelectedCountry(country);
    setPhoneError(null);
  };

  const handlePhoneCancel = () => {
    setPhone(phoneDigitsOnly);
    setSelectedCountry(null);
    setPhoneError(null);
    setVerificationCode("");
    setVerificationError(null);
    setVerificationStep("input");
    setEditPhone(0);
  };

  // ✅ Step 1: Save phone number temporarily (not verified yet)
  const handlePhoneSave = async () => {
    if (!selectedCountry || !phone || phone.length < 5) {
      setPhoneError("Please enter a valid phone number");
      return;
    }

    try {
      setVerificationLoading(true);
      const fullPhone = `+${selectedCountry.dialCode}${phone}`;

      // ✅ Update phone in Cognito (but NOT verified yet)
      if (updateUserAttributes) {
        await updateUserAttributes({
          phone_number: fullPhone,
          // Don't set phone_number_verified yet
        });
      }

      // ✅ Request verification code
      if (sendAttributeVerificationCode) {
        await sendAttributeVerificationCode("phone_number");
      }

      setVerificationStep("verify");
      setVerificationError(null);
    } catch (err) {
      setPhoneError("Failed to save phone number. Please try again.");
      console.error("Phone save failed:", err);
    } finally {
      setVerificationLoading(false);
    }
  };

  // ✅ Step 2: Verify the phone with the code
  const handlePhoneVerify = async () => {
    if (!verificationCode || verificationCode.length < 6) {
      setVerificationError("Please enter a valid verification code");
      return;
    }

    try {
      setVerificationLoading(true);

      // ✅ Verify the code with Cognito
      if (verifyUserAttribute) {
        await verifyUserAttribute("phone_number", verificationCode);
      }

      setVerificationStep("success");
      setVerificationError(null);

      // Close dialog after 2 seconds
      setTimeout(() => {
        setEditPhone(0);
        setPhone(phoneDigitsOnly);
        setSelectedCountry(null);
        setVerificationCode("");
        setVerificationStep("input");
      }, 2000);
    } catch (err) {
      setVerificationError("Invalid verification code. Please try again.");
      console.error("Verification failed:", err);
    } finally {
      setVerificationLoading(false);
    }
  };
  useEffect(() => {
    if (isAuthenticated && isRedirecting) {
      setIsRedirecting(false);
    }
  }, [isAuthenticated, isRedirecting, setIsRedirecting]);

  useEffect(() => {
    if (isLoading) return;
    if (hasCheckedAuth.current) return;

    hasCheckedAuth.current = true;
  }, [isLoading, isAuthenticated]);

  if (!isAuthenticated && !isRedirecting) {
    return <NotFound />;
  }
  if (isAuthenticated && isRedirecting) {
    return (
      <div className="auth__global__wrapper">
        <div className="authentication__component">
          <div className="auth_wrapper">
            <Heading
              heading="Login successful"
              text="Redirecting to your profile..."
            />
            <p className="text-size-regular">
              Please wait while we redirect you. If nothing happens,{" "}
              <a className="text_link" href="/profile">
                click here
              </a>
              .
            </p>
          </div>
        </div>
      </div>
    );
  }
  return (
    <div className="profile">
      <Heading
        heading={"Your profile"}
        text={"Manage your personal details and account settings."}
      />

      <div className="authentication__section">
        <p className="authentication__heading">Personal Details</p>
        <div className="authentication__grid">
          <Input
            id="firstName-input"
            name="First name"
            type="text"
            value={firstName}
            disabled={true}
            required
          />

          <Input
            id="lastName-input"
            name="Last name"
            type="text"
            value={lastName}
            disabled={true}
          />
        </div>

        <div className="authentication__grid">
          <Input
            id="email-input"
            name="E-mail"
            type="email"
            value={email}
            disabled={true}
          >
            {isEmailVerified && (
              <div className="authentication__icon_confirm"></div>
            )}
          </Input>

          {editPhone === 0 && (
            <Input
              id="phone-input"
              name="Phone number"
              type="tel"
              value={displayPhoneWithFlag()}
              disabled={true}
            >
              <div
                style={{
                  display: "flex",
                  gap: "8px",
                  alignItems: "center",
                }}
              >
                {cogntoPhoneRaw && isPhoneVerified && (
                  <div
                    className="authentication__icon_confirm"
                    title="Phone verified"
                  ></div>
                )}
                {cogntoPhoneRaw && !isPhoneVerified && (
                  <span
                    style={{
                      fontSize: "12px",
                      color: "var(--color-warning)",
                      fontWeight: 500,
                    }}
                  >
                    {/* // Verify number */}
                  </span>
                )}
                <AuthLink
                  onClick={() => setEditPhone(1)}
                  text={cogntoPhoneRaw ? "Change" : "Add"}
                />
              </div>
            </Input>
          )}
        </div>

        {editPhone === 1 && (
          <div className="authentication__section">
            <div className="authentication__heading__wrapper">
              <p className="authentication__heading">
                {verificationStep === "input"
                  ? cogntoPhoneRaw
                    ? "Change phone number"
                    : "Add phone number"
                  : verificationStep === "verify"
                  ? "Verify phone number"
                  : "Phone verified!"}
              </p>
              <p className="text-size-regular">
                {verificationStep === "input" &&
                  "Enter your country code and phone number."}
                {verificationStep === "verify" &&
                  "Enter the verification code sent to your phone."}
                {verificationStep === "success" &&
                  "Your phone number has been verified successfully."}
              </p>
            </div>

            {/* ✅ Step 1: Phone Input */}
            {verificationStep === "input" && (
              <>
                <div className="authentication__grid">
                  <PhoneInput
                    id="phone-input-edit"
                    value={phone}
                    onChange={handlePhoneChange}
                    onCountryChange={(country) => setSelectedCountry(country)}
                    error={phoneError || undefined}
                    defaultCountry={selectedCountry?.iso2 || "de"}
                  />
                </div>

                <div className="authentication__button__wrapper">
                  <button
                    className="button"
                    type="button"
                    onClick={handlePhoneSave}
                    disabled={verificationLoading || !phone || !selectedCountry}
                  >
                    {verificationLoading ? "Sending code..." : "Send Code"}
                  </button>
                  <button
                    className="button is-outline"
                    type="button"
                    onClick={handlePhoneCancel}
                    disabled={verificationLoading}
                  >
                    Cancel
                  </button>
                </div>
              </>
            )}

            {/* ✅ Step 2: Verification Code */}
            {verificationStep === "verify" && (
              <>
                <div className="authentication__grid">
                  <Input
                    id="verification-code-input"
                    name="Verification code"
                    type="text"
                    value={verificationCode}
                    onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                      const value = e.target.value
                        .replace(/\D/g, "")
                        .slice(0, 6);

                      setVerificationCode(value);
                      setVerificationError(null);
                    }}
                    placeholder="000000"
                    error={verificationError || undefined}
                    isValid={
                      verificationCode.length === 6 && !verificationError
                    }
                    disabled={verificationLoading}
                    required
                  />
                </div>

                <div className="authentication__button__wrapper">
                  <button
                    className="button"
                    type="button"
                    onClick={handlePhoneVerify}
                    disabled={
                      verificationLoading || verificationCode.length < 6
                    }
                  >
                    {verificationLoading ? "Verifying..." : "Verify"}
                  </button>
                  <button
                    className="button is-outline"
                    type="button"
                    onClick={handlePhoneCancel}
                    disabled={verificationLoading}
                  >
                    Cancel
                  </button>
                </div>
              </>
            )}

            {/* ✅ Step 3: Success */}
            {verificationStep === "success" && (
              <div
                style={{
                  padding: "16px",
                  backgroundColor: "rgba(33, 128, 141, 0.1)",
                  borderRadius: "8px",
                  border: "1px solid var(--color-success)",
                  display: "flex",
                  alignItems: "center",
                  gap: "8px",
                  color: "var(--color-success)",
                }}
              >
                <span style={{ fontSize: "20px" }}>✓</span>
                <span>Phone number verified successfully!</span>
              </div>
            )}
          </div>
        )}
      </div>

      {/* Security section (existing password code) */}
      <div className="authentication__section">
        <p className="authentication__heading">Security Details</p>

        <Input
          id="password-input"
          name="Password"
          type="password"
          value="*********"
          disabled={true}
        >
          {editPassword === 0 && (
            <AuthLink onClick={() => setEditPassword(1)} text={"Change"} />
          )}
        </Input>
      </div>

      {editPassword === 1 && (
        <div className="authentication__section">
          <div className="authentication__heading__wrapper">
            <p className="authentication__heading">Change password</p>
            <p className="text-size-regular">
              At least 8 characters long with a combination of uppercase and
              lowercase letters, numbers and a special character.
            </p>
          </div>
          <form
            className="authentication__section"
            onSubmit={handlePasswordUpdate}
          >
            <Input
              id="password-input"
              name="Current password"
              type="password"
              value={currentPassword}
              onChange={handleCurrentPasswordChange}
              onBlur={handleCurrentPasswordBlur}
              error={errors.currentPassword || authError || undefined}
              isValid={!errors.currentPassword && currentPassword.length > 0}
              disabled={isLoading}
              required
            />

            <div className="authentication__grid">
              <Input
                id="new-password-input"
                name="New password"
                type="password"
                value={newPassword}
                onChange={handleNewPasswordChange}
                onBlur={handleNewPasswordBlur}
                error={errors.newPassword || undefined}
                isValid={!errors.newPassword && newPassword.length > 0}
                disabled={isLoading}
                required
              />
              <Input
                id="new-password-confirm"
                name="Confirm new password"
                type="password"
                value={confirmPassword}
                onChange={handleConfirmPasswordChange}
                onBlur={handleConfirmPasswordBlur}
                error={errors.confirmPassword || undefined}
                isValid={!errors.confirmPassword && confirmPassword.length > 0}
                disabled={isLoading}
                required
              />
            </div>

            <div className="authentication__button__wrapper">
              <button
                className="button"
                type="submit"
                disabled={isLoading || !isFormValid}
              >
                {isLoading ? "Saving..." : "Save Changes"}
              </button>
              <button
                className="button is-outline"
                type="button"
                onClick={handleCancel}
                disabled={isLoading}
              >
                Cancel
              </button>
            </div>
          </form>
        </div>
      )}

      {/* Delete profile section */}
      <div className="authentication__section">
        <div className="authentication__heading__wrapper">
          <p className="authentication__heading">Delete your profile</p>
          <p className="text-size-regular">
            Oh no! Are you sure you want to leave? You won't be able to restore
            your account and will lose all your data. If you need a break, you
            can temporarily log out of your profile.
          </p>
        </div>
        <div>
          <button
            className="button is-danger"
            onClick={() => {
              setOpenPopup(!openPopup);
            }}
            disabled={isLoading}
          >
            Delete Account
          </button>
        </div>
      </div>
      <div className={`popup ${openPopup && "popup__show"}`}>
        <div className="popup__bg"></div>
        <div className="popup__component">
          <button
            onClick={() => {
              setOpenPopup(!openPopup);
            }}
            className="popup__close"
          >
            <CloseIcon />
          </button>
          <img
            className="popup__image"
            src="https://cdn.prod.website-files.com/6710c34695543e421832e7c4/693001ee8733a149543201eb_wepik-export-20230912090324Uikp%201.png"
          />
          <div className="popup__text__wrapper">
            <p className="popup__heading">We're sad to see you go</p>
            <p className="popup__text">
              If you choose to delete your profile, you will no longer have
              access to Daleel and lose all your data (including points!). You
              can still log out of your profile if you want a temporary break.
            </p>
            <p className="popup__text">
              If you signed up on Daleel using your Apple ID, please visit this
              page for more information.
            </p>
          </div>
          <div className="popup__button__wrapper">
            <button
              onClick={() => {
                if (
                  window.confirm(
                    "Are you sure you want to delete your account? This cannot be undone."
                  )
                ) {
                  deleteProfile();
                }
              }}
              className="button is-red"
            >
              Delete your profile
            </button>
            <button
              className="button is-red is-outline"
              onClick={() => {
                setOpenPopup(!openPopup);
              }}
            >
              Cancel
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Profile;
